import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getPersistedChatSessionId, setPersistedChatSessionId } from "../../utils/chatSession";
import { getCustomerToken, joinPublicStorefrontUrl } from "../../api/client";
import "./ChatWidget.css";

function isChatGloballyDisabled() {
  const v = process.env.REACT_APP_CHAT_ENABLED;
  return v === "0" || v === "false";
}

function hideChatForPath(pathname) {
  if (!pathname) return true;
  if (pathname.startsWith("/admin")) return true;
  if (pathname === "/payment" || pathname.startsWith("/payment/")) return true;
  return false;
}

function formatRichChunks(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((chunk, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(chunk);
    if (m) return <strong key={`b-${i}`}>{m[1]}</strong>;
    const lines = chunk.split("\n").map((line, li) =>
      li === 0 ? (
        <React.Fragment key={`nl-${i}-${li}-a`}>{line}</React.Fragment>
      ) : (
        <React.Fragment key={`nl-${i}-${li}-b`}>
          <br />
          {line}
        </React.Fragment>
      )
    );
    return <span key={`s-${i}`}>{lines}</span>;
  });
}

export default function ChatWidget() {
  const location = useLocation();
  const panelId = useId();
  const scrollRef = useRef(null);
  const panelOpenRef = useRef(false);
  const bootstrapStartedRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionIdState] = useState(() => getPersistedChatSessionId());
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    panelOpenRef.current = open;
    if (open) setUnread(0);
  }, [open]);

  const syncSession = useCallback((id) => {
    if (id && String(id).length >= 8) {
      setPersistedChatSessionId(id);
      setSessionIdState(String(id));
    }
  }, []);

  const mergeAssistantResponse = useCallback(
    (data) => {
      const incoming = Array.isArray(data.messages) ? data.messages : [];
      const rows = incoming.map((m, idx) => ({
        ...m,
        key: `${Date.now()}-${idx}-${Math.random().toString(36).slice(2)}`,
      }));

      const assistantAdds = incoming.filter((x) => x.role !== "user").length;
      setMessages((prev) => [...prev, ...rows]);
      setQuickReplies(Array.isArray(data.quickReplies) ? data.quickReplies : []);

      if (data.sessionId) syncSession(data.sessionId);

      if (!panelOpenRef.current && assistantAdds > 0) {
        setUnread((u) => u + assistantAdds);
      }
    },
    [syncSession]
  );

  const scrollBottom = () => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  useEffect(() => {
    scrollBottom();
  }, [messages, open, loading]);

  useEffect(() => {
    if (!open || bootstrapStartedRef.current || isChatGloballyDisabled()) return;

    const runBootstrap = async () => {
      if (messages.length > 0) {
        bootstrapStartedRef.current = true;
        return;
      }
      bootstrapStartedRef.current = true;
      setLoading(true);
      setError("");
      try {
        const token = getCustomerToken();
        const res = await fetch(joinPublicStorefrontUrl("/api/chat/bootstrap"), {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Could not load chat");
        mergeAssistantResponse(data);
      } catch (e) {
        setError(String(e.message || "Chat unavailable."));
        bootstrapStartedRef.current = false;
      } finally {
        setLoading(false);
      }
    };
    runBootstrap();
  }, [open, mergeAssistantResponse, messages.length]);

  const sendText = async (text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed || loading) return;

    setError("");
    setQuickReplies([]);
    const userBubble = {
      role: "user",
      content: trimmed,
      key: `user-${Date.now()}`,
    };
    setMessages((prev) => [...prev, userBubble]);
    if (!panelOpenRef.current) setOpen(true);

    setLoading(true);
    try {
      const token = getCustomerToken();
      const sid = sessionId || getPersistedChatSessionId();
      const res = await fetch(joinPublicStorefrontUrl("/api/chat/message"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ sessionId: sid, text: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Message failed.");
      mergeAssistantResponse(data);
    } catch (e) {
      setError(String(e.message || "Failed to send."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  if (isChatGloballyDisabled() || hideChatForPath(location.pathname)) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="gem-chat-launcher"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat assistant" : "Open chat assistant — Gem Fashion help"}
      >
        {unread > 0 && !open ? (
          <span className="gem-chat-launcher__badge" aria-label="New messages">{unread > 9 ? "9+" : unread}</span>
        ) : null}
        <span className="gem-chat-launcher__glow" aria-hidden />
        <span className="gem-chat-launcher__inner">
          <svg className="gem-chat-launcher__icon" viewBox="0 0 24 24" aria-hidden fill="none">
            <path
              d="M12 2.85 20.05 9.75 12 21.15 3.95 9.75z"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <path
              d="M12 2.85V12.1M4.15 9.75H19.85M7.5 7.2 L12 12.1 L16.5 7.2"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity="0.5"
            />
          </svg>
        </span>
      </button>

      {open ? (
        <section id={panelId} className="gem-chat-panel" role="dialog" aria-label="Gem Assistant chat">
          <div className="gem-chat-panel__head">
            <div>
              <strong>Gem Assistant</strong>
              <div style={{ fontSize: "12px", opacity: 0.85 }}>Rentals · sizing · orders</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close chat">
              ×
            </button>
          </div>

          <div className="gem-chat-scroll" ref={scrollRef} aria-live="polite">
            {messages.map((m) =>
              m.role === "user" ? (
                <div key={m.key} className="gem-chat-msg gem-chat-msg--user">
                  {m.content}
                </div>
              ) : (
                <div key={m.key} className="gem-chat-msg gem-chat-msg--assist">
                  <div>{formatRichChunks(m.content)}</div>
                  {Array.isArray(m.links) && m.links.length ? (
                    <div className="gem-chat-msg__links">
                      {m.links.map((l) =>
                        /^https?:\/\//i.test(l.href) ? (
                          <a key={l.href + l.label} href={l.href} target="_blank" rel="noopener noreferrer">
                            {l.label}
                          </a>
                        ) : (
                          <Link key={l.href + l.label} to={l.href.startsWith("/") ? l.href : `/${l.href}`} onClick={() => setOpen(false)}>
                            {l.label}
                          </Link>
                        )
                      )}
                    </div>
                  ) : null}
                </div>
              )
            )}
            {loading ? (
              <div className="gem-chat-msg gem-chat-msg--assist" aria-busy="true">
                <div className="gem-chat-typing" aria-hidden>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            ) : null}
            {error ? (
              <div className="gem-chat-msg gem-chat-msg--assist" role="alert" style={{ color: "#c62828" }}>
                {error}
              </div>
            ) : null}
          </div>

          <div className="gem-chat-quick">
            {quickReplies.map((qr) => (
              <button key={`${qr.label}-${qr.send}`} type="button" disabled={loading} onClick={() => sendText(qr.send)}>
                {qr.label}
              </button>
            ))}
          </div>

          <form
            className="gem-chat-form"
            onSubmit={(e) => {
              e.preventDefault();
              sendText(input);
              setInput("");
            }}
          >
            <input
              type="text"
              placeholder="Ask about rentals…"
              value={input}
              aria-label="Message"
              disabled={loading}
              onChange={(e) => setInput(e.target.value)}
            />
            <button type="submit" disabled={loading || !input.trim()}>
              Send
            </button>
          </form>
        </section>
      ) : null}
    </>
  );
}
